<template>
  <!-- CD-12 ACCOUNT PAYMENT DETAILS -->
  <div class="w-full md:max-w-[512px]">
    <AddCardConfirmation
      :open="showConfirmation"
      @update:open="showConfirmation = $event"
      @update:on-accept="onConfirmAddCard"
    />

    <div v-if="showForm" class="space-y-4">
      <div v-if="errorMessage" class="text-red-500 text-sm">
        {{ errorMessage }}
      </div>
      <LayupSpinner v-if="isLoading" />
      <div v-else id="shopper-payment-form" :class="{ 'h-[550px]': checkoutFormLoaded }"></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import LayupSpinner from '@/components/common/LayupSpinner.vue';
import AddCardConfirmation from '@/components/merchant/settings/billing-fees/AddCardConfirmation.vue';
import { BASE_URL, EMBEDDED_ENTITY_ID } from '@/config';
import { NetworkError } from '@/errors';
import { createCard, verifyCard } from '@/services/cards';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import {
  waitForCheckoutSDK,
  waitForElement,
  type PeachCheckoutEvent,
  type PeachCompletedCheckoutEvent,
  type PeachErrorCheckoutEvent,
} from '@/utils/peach.utils';
import { ref } from 'vue';
import type { ModelCardRequest } from '@layup/api-sdk';

const emit = defineEmits(['card:added', 'card:cancelled']);

const showConfirmation = ref(true);
const showForm = ref(false);
const isLoading = ref(false);
const errorMessage = ref('');
const checkoutFormLoaded = ref(false);

const onConfirmAddCard = () => {
  showConfirmation.value = false;
  showForm.value = true;
  loadCheckoutForm();
};

const loadCheckoutForm = async () => {
  let checkoutId: string | undefined = undefined;
  isLoading.value = true;

  try {
    const request: ModelCardRequest = {
      defaultCard: true,
      redirectUrl: `${BASE_URL}/shopper`,
    };

    const createdCard = await createCard(request);
    checkoutId = createdCard.checkoutId;
    addMetric('add_card', { status: 'success', channel: 'shopper' });
  } catch (error) {
    if (error instanceof NetworkError) {
      addMetric('add_card', { status: 'network_error', channel: 'shopper' });
      createWarning(error);
    } else {
      addMetric('add_card', { status: 'failure', channel: 'shopper' });
      createException(error);
    }
    errorMessage.value = `Error adding card: ${error}`;
    isLoading.value = false;
    return;
  }

  if (!checkoutId) {
    errorMessage.value = 'Failed to create checkout session.';
    return;
  }

  try {
    const sdkLoaded = await waitForCheckoutSDK();
    isLoading.value = false;

    if (!sdkLoaded) {
      addMetric('add-card', { status: 'peach_sdk_load_failure', channel: 'shopper' });
      createException({
        message: 'Checkout SDK failed to load after multiple attempts.',
        checkoutId,
        userAgent: navigator.userAgent,
      });
      errorMessage.value =
        'Our payment provider failed to load. Please check your internet connection and try again. If the problem persists, please try a different browser.';
      return;
    }

    const checkout = (window as any).Checkout.initiate({
      checkoutId,
      key: EMBEDDED_ENTITY_ID,
      events: {
        onCompleted: (event: PeachCompletedCheckoutEvent) => {
          addMetric('peach_widget', {
            status: 'success',
            result: event.result.code,
            value: event.amount,
            channel: 'shopper',
          });
          verifyCard(checkoutId!).then(() => {
            emit('card:added');
          });
        },
        onCancelled: (event: PeachCheckoutEvent) => {
          addMetric('peach_widget', { status: 'cancelled', result: event.result.code, channel: 'shopper' });
          emit('card:cancelled');
        },
        onExpired: (event: PeachCheckoutEvent) => {
          addMetric('peach_widget', { status: 'expired', result: event.result.code, channel: 'shopper' });
          emit('card:cancelled');
        },
        onError: (event: PeachErrorCheckoutEvent) => {
          const error = new Error('Peach widget error.');
          createWarning(error, {
            checkoutId,
            errorCode: event.result.code,
            errorDescription: event.result.description,
            userAgent: navigator.userAgent,
          });
          addMetric('peach_widget', { status: 'error', result: event.result.code, channel: 'shopper' });
          errorMessage.value =
            'Something went wrong while processing the payment. Please try again or contact support.';
        },
      },
      options: {
        theme: {
          brand: {
            primary: '#0f7d88',
          },
        },
      },
    });

    const paymentFormElement = await waitForElement('#shopper-payment-form');
    checkout.render(paymentFormElement);
    checkoutFormLoaded.value = true;
  } catch (error) {
    addMetric('peach_widget', { status: 'peach_sdk_load_failure', channel: 'shopper' });
    createException({
      message: 'Failed to initialize checkout form.',
      checkoutId,
      error,
      userAgent: navigator.userAgent,
    });
    errorMessage.value = 'Unable to load the payment form. Please try again or contact support.';
  }
};
</script>
